import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { Link } from "react-router-dom";
import { Handshake, ShieldCheck, BadgeCheck, Globe2, FileSpreadsheet, CheckCircle2, ArrowRight, Building2 } from "lucide-react";
import { PARTNER_OFFERINGS, PARTNERS } from "../mock";

const partnerSlug = (name) => name.toLowerCase().replace(/[()]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const PROCUREMENT_PILLARS = [
  { icon: ShieldCheck, title: "Governance & Compliance", desc: "Vendor-authorised curricula, audit-ready training records and contractual SLA-backed delivery." },
  { icon: FileSpreadsheet, title: "Procurement-Friendly Commercials", desc: "Volume pricing, multi-year MSAs, consolidated GST invoicing and budget-aligned payment terms." },
  { icon: Globe2, title: "Global Delivery Footprint", desc: "Single point of contact across 60+ countries, 15+ languages and multiple delivery modalities." },
  { icon: BadgeCheck, title: "Official Certification Pathways", desc: "Bundled exam vouchers, sandbox access and post-training mentor support to maximise pass rates." }
];

const PartnerOfferingsPage = () => {
  return (
    <div className="bg-white min-h-screen">
      <Header/>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#15348C] via-[#1E47A8] to-[#15348C] text-white">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-[#6BBE32]/10 rounded-full blur-3xl"/>
        <div className="relative max-w-7xl mx-auto px-4 py-20 grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-3">
            <Reveal variant="left">
              <div className="inline-flex items-center gap-2 bg-[#6BBE32]/20 border border-[#8BD835]/30 px-3 py-1.5 rounded-full text-xs font-semibold mb-5">
                <Handshake size={14} className="text-[#8BD835]"/> Authorised Training Partner Programs
              </div>
            </Reveal>
            <Reveal variant="left" delay={100}>
              <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
                Partner Programs &amp; <span className="text-[#8BD835]">Certifications</span>
              </h1>
            </Reveal>
            <Reveal variant="left" delay={200}>
              <p className="mt-5 text-gray-300 text-lg max-w-xl">
                We support organisations in preparing teams for globally recognised certifications aligned to enterprise roles and technologies.
              </p>
            </Reveal>
            <Reveal variant="left" delay={280}>
              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm text-gray-200">
                <Pill icon={BadgeCheck}>Vendor-Authorised Curriculum</Pill>
                <Pill icon={ShieldCheck}>Official Exam Vouchers</Pill>
                <Pill icon={Globe2}>Global Delivery</Pill>
              </div>
            </Reveal>
            <Reveal variant="left" delay={340}>
              <div className="mt-8">
                <Link to="/contact" className="sg-btn-glow inline-flex items-center gap-2 bg-[#6BBE32] hover:bg-[#4F9622] text-white font-semibold px-6 h-12 rounded-md shadow-lg shadow-[#6BBE32]/30 transition-colors">
                  View Partner Programs &amp; Certifications <ArrowRight size={17}/>
                </Link>
              </div>
            </Reveal>
          </div>

          <Reveal variant="right" delay={150} className="lg:col-span-2">
            <div className="grid grid-cols-3 gap-3">
              {PARTNER_OFFERINGS.slice(0, 9).map(p => (
                <Link key={p.name} to={`/offerings/partner/${partnerSlug(p.name)}`} className="bg-white/8 backdrop-blur border border-white/15 rounded-xl p-3 h-20 flex items-center justify-center text-center hover:bg-white/15 hover:border-[#8BD835]/40 transition-colors cursor-pointer">
                  <span className="text-white/90 text-xs font-semibold leading-tight">{p.name}</span>
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Procurement pillars */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">WHY ENTERPRISES CHOOSE AUTHORISED PARTNERS</div>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">Built for L&amp;D, Procurement and Risk teams.</h2>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PROCUREMENT_PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <Reveal key={p.title} variant="up" delay={i * 80}>
                <div className="sg-card group bg-white border border-gray-100 rounded-2xl p-6 h-full">
                  <div className="w-11 h-11 rounded-lg bg-[#6BBE32]/15 group-hover:bg-[#6BBE32] flex items-center justify-center mb-4 transition-colors">
                    <Icon size={20} className="text-[#6BBE32] group-hover:text-white transition-colors"/>
                  </div>
                  <h4 className="font-bold text-[#15348C]">{p.title}</h4>
                  <p className="text-sm text-gray-600 mt-2 leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Partner ecosystem */}
      <section className="bg-[#F8F9FA] py-16">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-10">
              <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">PARTNER ECOSYSTEM</div>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#15348C]">A single contract. 40+ certifying bodies.</h2>
              <p className="mt-3 text-gray-600">Consolidate vendor-led training across the world’s most relevant technology providers under one partnership.</p>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PARTNER_OFFERINGS.map((p, i) => (
              <Reveal key={p.name} variant="up" delay={(i % 6) * 60}>
                <Link to={`/offerings/partner/${partnerSlug(p.name)}`} id={partnerSlug(p.name)} className="sg-card group bg-white border border-gray-100 rounded-2xl p-6 h-full scroll-mt-28 block">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#15348C] text-white flex items-center justify-center font-bold text-xs">{(p.logoName || p.name).slice(0,2).toUpperCase()}</div>
                      <h3 className="font-bold text-[#15348C] group-hover:text-[#6BBE32] transition-colors">{p.name}</h3>
                    </div>
                    <span className="text-xs bg-[#6BBE32]/15 text-[#4F9622] px-2 py-1 rounded-full font-semibold whitespace-nowrap">{p.courses.length} programs</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {p.courses.slice(0,4).map(c => (
                      <span key={c} className="text-[11px] bg-[#F4F7FB] text-[#15348C] px-2.5 py-1 rounded-full">{c}</span>
                    ))}
                  </div>
                  <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#F57C20] sg-link">
                    View partner page <ArrowRight size={13}/>
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Partners strip */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <Reveal>
          <div className="text-center mb-10">
            <div className="text-xs font-bold tracking-widest text-[#F57C20] mb-3">CERTIFYING BODIES</div>
            <h2 className="text-2xl lg:text-3xl font-bold text-[#15348C]">Recognised globally by the world’s leading vendors</h2>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {PARTNERS.map((p, i) => (
            <Reveal key={p.name} variant="scale" delay={(i % 6) * 50}>
              <div className="bg-white border border-gray-100 rounded-xl h-24 flex items-center justify-center p-4 hover:border-[#6BBE32] hover:shadow-lg hover:-translate-y-1 transition-all group">
                {p.logo ? (
                  <img src={p.logo} alt={p.name} className="max-h-12 max-w-[120px] object-contain grayscale group-hover:grayscale-0 opacity-70 group-hover:opacity-100 transition-all"
                    onError={(e) => { e.target.style.display="none"; e.target.parentElement.innerHTML += `<span class='text-[#15348C] font-bold text-sm'>${p.name}</span>`; }}
                  />
                ) : (
                  <span className="text-[#15348C] font-bold text-sm">{p.name}</span>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 pb-20">
        <Reveal variant="scale">
          <div className="bg-[#15348C] rounded-3xl p-10 lg:p-14 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
            <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#F57C20]/20 rounded-full blur-3xl"/>
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">FOR L&amp;D &amp; PROCUREMENT</div>
                <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight">Consolidate your vendor-led training under one trusted partner.</h2>
                <p className="mt-3 text-gray-300">Cut procurement overhead, unify reporting, and unlock partner-level pricing across 40+ certifying bodies.</p>
              </div>
              <div className="flex flex-wrap gap-3 lg:justify-end">
                <Link to="/contact" className="sg-btn-glow bg-[#F57C20] hover:bg-[#D86810] text-white font-semibold px-6 py-3 rounded-md transition-colors">Talk to Our Advisor</Link>
                <Link to="/contact" className="border-2 border-white/40 text-white font-semibold px-6 py-3 rounded-md hover:bg-white/10 hover:border-[#8BD835] transition-colors">Request Corporate Proposal</Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <Footer/>
    </div>
  );
};

const Pill = ({ icon: Icon, children }) => (
  <span className="flex items-center gap-1.5"><Icon size={16} className="text-[#8BD835]"/> {children}</span>
);

export default PartnerOfferingsPage;
